<template>
  <div>
    <b-button-group
      v-bind:vertical="showVertical"
      v-bind:class="{ 'flex-wrap': true, 'w-100': useFullWidth }"
    >
      <b-button
        variant="outline-dark"
        v-bind:class="'btnToggleVisibility' + quiz.id"
        @click="changeVisibility"
      >
        <b-icon-lock-fill v-if="quiz.public == 0" />
        <b-icon-unlock v-else />
        <span v-if="useFullWidth" class="ml-1">
          <span v-if="quiz.public == 0">Private</span>
          <span v-else>Public</span>
        </span>
      </b-button>
      <b-button
        variant="outline-dark"
        @click="changeCalendarVisibility"
        v-bind:class="'btnToggleVisibilityCalendar' + quiz.id"
      >
        <b-icon-calendar-fill v-if="quiz.show_in_calendar == 1" />
        <b-icon-calendar v-else />
        <span v-if="useFullWidth" class="ml-1">
          <span v-if="quiz.show_in_calendar == 1">Listed</span>
          <span v-else>Unlisted</span>
        </span>
      </b-button>
      <b-button
        variant="outline-dark"
        v-bind:class="'btnLinkStream' + quiz.id"
        @click="changeUrl"
      >
        <b-icon-camera-video-fill v-if="quiz.link_to_stream" />
        <b-icon-camera-video v-else />
        <span v-if="useFullWidth" class="ml-1">
          <span v-if="quiz.link_to_stream">Set</span>
          <span v-else>Not set</span>
        </span>
      </b-button>
      <b-button
        variant="outline-dark"
        v-bind:class="'btnLinkWeb' + quiz.id"
        @click="changeWeb"
      >
        <b-icon-tv-fill v-if="quiz.link_to_website" />
        <b-icon-tv v-else />
        <span v-if="useFullWidth" class="ml-1">
          <span v-if="quiz.link_to_website">Set</span>
          <span v-else>Not set</span>
        </span>
      </b-button>
      <b-button
        variant="outline-dark"
        v-bind:class="'btnCopySpectate' + quiz.id"
        @click="copySpectate"
      >
        <b-icon-clipboard-data class="mr-1" />
        <b-icon-eye-fill />
      </b-button>
      <b-button
        variant="outline-dark"
        v-bind:class="'btnCopyJoin' + quiz.id"
        @click="copyJoin"
      >
        <b-icon-clipboard-data class="mr-1" />
        <b-icon-person-plus-fill />
      </b-button>
    </b-button-group>
    <!-- TIPPY -->
    <TopquizTippy
      noHeader="true"
      v-bind:placement="tippyPlacement"
      v-bind:selector="'.btnToggleVisibility' + quiz.id"
    >
      <p>{{ explainVisibility }}</p>
    </TopquizTippy>
    <TopquizTippy
      noHeader="true"
      v-bind:placement="tippyPlacement"
      v-bind:selector="'.btnToggleVisibilityCalendar' + quiz.id"
    >
      <p>{{ explainCalendarVisibility }}</p>
    </TopquizTippy>
    <TopquizTippy
      noHeader="true"
      v-bind:placement="tippyPlacement"
      v-bind:selector="'.btnLinkStream' + quiz.id"
    >
      <p v-if="quiz.link_to_stream">{{ quiz.link_to_stream }}</p>
      <p v-else>
        You can set a link to your video stream here to make it easier for
        quizzers to find it.
      </p>
    </TopquizTippy>
    <TopquizTippy
      noHeader="true"
      v-bind:placement="tippyPlacement"
      v-bind:selector="'.btnLinkWeb' + quiz.id"
    >
      <p v-if="quiz.link_to_website">{{ quiz.link_to_website }}</p>
      <p v-else>
        You can set a link to your website here. This will be shown in the
        calendar.
      </p>
    </TopquizTippy>
    <TopquizTippy
      noHeader="true"
      v-bind:placement="tippyPlacement"
      v-bind:selector="'.btnCopySpectate' + quiz.id"
    >
      <p>Copy a link for people who want to spectate the quiz.</p>
    </TopquizTippy>
    <TopquizTippy
      noHeader="true"
      v-bind:placement="tippyPlacement"
      v-bind:selector="'.btnCopyJoin' + quiz.id"
    >
      <p>Copy a link for people who want to join the quiz.</p>
    </TopquizTippy>
    <!-- DIALOGS -->
    <DialogQuizStreamUrl
      v-model="editingUrl"
      title="Change URL to video stream"
      v-bind:url="quiz.link_to_stream"
      @ok="urlChanged"
    />
    <DialogQuizStreamUrl
      v-model="editingWeb"
      title="Change URL to quiz website"
      v-bind:url="quiz.link_to_website"
      @ok="webChanged"
    />
    <DialogError v-model="errorVisible">{{ errorMessage }}</DialogError>
    <DialogInfo v-model="infoVisible">
      <p>{{ infoMessage }}</p>
      <b-badge variant="warning" pill v-if="infoWarning" class="mr-2"
        >Warning</b-badge
      >
      <span variant="warning" v-if="infoWarning">{{ infoWarning }}</span>
    </DialogInfo>
  </div>
</template>

<script>
import TopquizTippy from "@/components/TopquizTippy";
import DialogQuizStreamUrl from "@/components/host/DialogQuizStreamUrl";
import DialogError from "@/components/DialogError";
import DialogInfo from "@/components/DialogInfo";
import explanations from "@/assets/explanations.js";
import { ClipBoard } from "@/assets/convenience.js";

export default {
  name: "HostQuizItemControls",
  props: {
    quiz: { default: {} },
    vertical: {
      default: "never"
    },
    fullWidth: {
      default: "never"
    }
  },
  components: {
    TopquizTippy,
    DialogQuizStreamUrl,
    DialogError,
    DialogInfo
  },
  data() {
    return {
      editingUrl: false,
      editingWeb: false,
      errorVisible: false,
      errorMessage: "",
      infoVisible: false,
      infoMessage: "",
      infoWarning: ""
    };
  },
  computed: {
    bsSize() {
      return this.$store.state.bootstrapScreenSize;
    },
    tippyPlacement() {
      return this.showVertical ? "right" : "bottom";
    },
    useFullWidth() {
      return this.fullWidth && this.fullWidth.includes(this.bsSize);
    },
    showVertical() {
      return this.vertical && this.vertical.includes(this.bsSize);
    },
    quizVisibilityString() {
      return explanations.visibility_names[this.quiz.public];
    },
    explainVisibility() {
      return explanations.visibility[this.quiz.public];
    },
    explainCalendarVisibility() {
      return explanations.visibility_calendar[this.quiz.show_in_calendar];
    }
  },
  methods: {
    changeStatus() {
      this.editingStatus = true;
    },
    statusChanged(ev, status) {
      if (this.quiz.status == status) {
        return;
      }
      this.$topquiz.sendCommand(
        "host_changeQuizStatus",
        this.$topquiz.SessionKey,
        this.quiz.id,
        status
      );
    },
    changeVisibility() {
      this.$topquiz.sendCommand(
        "host_changeQuizVisibility",
        this.$topquiz.SessionKey,
        this.quiz.id,
        1 - this.quiz.public
      );
    },
    changeCalendarVisibility() {
      this.sentShowCalendar = true;
      this.$topquiz.sendCommand(
        "host_showInCalendar",
        this.$topquiz.SessionKey,
        this.quiz.id,
        1 - this.quiz.show_in_calendar
      );
    },
    changeUrl() {
      this.editingUrl = true;
    },
    urlChanged(ev, url) {
      this.editingUrl = false;
      if (this.quiz.link_to_stream == url) {
        return;
      }
      this.$topquiz.sendCommand(
        "host_changeQuizStreamUrl",
        this.$topquiz.SessionKey,
        this.quiz.id,
        url
      );
    },
    changeWeb() {
      this.editingWeb = true;
    },
    webChanged(ev, web) {
      this.editingWeb = false;
      if (this.quiz.link_to_website == web) {
        return;
      }
      this.$topquiz.sendCommand(
        "host_changeQuizWebsite",
        this.$topquiz.SessionKey,
        this.quiz.id,
        web
      );
    },
    copySpectate() {
      var link = this.$topquiz.getSpectateLinkFromCode(this.quiz.spectate_code);
      ClipBoard.setText(link);
      this.infoMessage = "Copied spectate link to clipboard.";
      this.infoVisible = true;
    },
    copyJoin() {
      var link = this.$topquiz.getJoinLinkFromCode(this.quiz.join_code);
      ClipBoard.setText(link);
      this.infoMessage = "Copied join link to clipboard.";
      if (
        this.quiz.status < this.$tqConstants["QUIZ_STATUS_OPEN"] ||
        this.quiz.status >= this.$tqConstants["QUIZ_STATUS_RUNNING_CLOSED"]
      ) {
        this.infoWarning = "Given its status, your quiz cannot be joined.";
      }

      this.infoVisible = true;
    }
  },
  created() {
    this.$topquiz.on(this, "host_showInCalendar_error", params => {
      if (this.sentShowCalendar) {
        var error = params[0];
        if (error == -1) {
          error =
            "You must set a date before you can list the item in the calendar.";
        }
        this.errorMessage = error;
        this.errorVisible = true;
        this.sentShowCalendar = false;
      }
    });
    this.$topquiz.on(this, "quizVisibilityChanged", params => {
      if (params[0] == this.quiz.id) {
        this.quiz.public = params[1];
      }
    });
    this.$topquiz.on(this, "quizCalendarVisibilityChanged", params => {
      if (params[0] == this.quiz.id) {
        this.quiz.show_in_calendar = params[1];
        this.sentShowCalendar = false;
      }
    });
    this.$topquiz.on(this, "quizStreamUrlChanged", params => {
      if (params[0] == this.quiz.id) {
        this.quiz.link_to_stream = params[1];
      }
    });
    this.$topquiz.on(this, "quizWebsiteChanged", params => {
      if (params[0] == this.quiz.id) {
        this.quiz.link_to_website = params[1];
      }
    });
  },
  destroyed() {
    this.$topquiz.unsubscribe(this);
  }
};
</script>
